import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Divider,
  Form,
  Grid,
  InputNumber,
  Select,
  Space,
  Typography,
  Input,
  Message,
  Tooltip,
} from "@arco-design/web-react";
import { IconCopy, IconCaretDown, IconImport } from "@arco-design/web-react/icon";
import { copy } from "../utils/copy";
import { usePasswordCheck } from "../hooks/usePasswordCheck";

const { Row, Col } = Grid;
const { Option } = Select;
const TextArea = Input.TextArea;

const defaultRow = () => ({ func: "04", phi: 0, len: 0, r: 0 });

const STORAGE_KEY = "dpg_values_v2";
const IMPORT_KEY = "tangent_to_dresser_export";
const IMPORT_READY_KEY = "tangent_to_dresser_ready";
const IMPORT_START_ROW_KEY = "tangent_to_dresser_start_row";

const DresserProgramGenerator = ({ onPasswordInvalid, isActive }) => {
  const { checkPasswordBeforeAction } = usePasswordCheck(onPasswordInvalid);
  const [oNum, setONum] = useState();
  const [phiMax, setPhiMax] = useState();
  const [phiMin, setPhiMin] = useState();
  const [phiCut, setPhiCut] = useState();
  const [mx, setMx] = useState();

  const [rows, setRows] = useState(Array.from({ length: 20 }, () => defaultRow()));
  const [program, setProgram] = useState("");
  const [importStartRow, setImportStartRow] = useState(2);

  // helpers for localStorage persistence
  const loadAll = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  const saveAll = (data) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // ignore
    }
  };
  const normalizeRows = (r) => {
    const base = Array.from({ length: 20 }, () => defaultRow());
    if (!Array.isArray(r)) return base;
    const out = base.slice();
    for (let i = 0; i < Math.min(20, r.length); i++) {
      const it = r[i] || {};
      out[i] = {
        func: it.func || "04",
        phi: Number.isFinite(Number(it.phi)) ? Number(it.phi) : 0,
        len: Number.isFinite(Number(it.len)) ? Number(it.len) : 0,
        r: Number.isFinite(Number(it.r)) ? Number(it.r) : 0,
      };
    }
    return out;
  };

  // initial restore
  useEffect(() => {
    const saved = loadAll();
    if (saved) {
      if ("oNum" in saved) setONum(saved.oNum);
      if ("phiMax" in saved) setPhiMax(saved.phiMax);
      if ("phiMin" in saved) setPhiMin(saved.phiMin);
      if ("phiCut" in saved) setPhiCut(saved.phiCut);
      if ("mx" in saved) setMx(saved.mx);
      if ("rows" in saved) setRows(normalizeRows(saved.rows));
    }

    // 如果组件初始时就是激活状态，检查待导入数据
    if (isActive) {
      const importReady = localStorage.getItem(IMPORT_READY_KEY);
      if (importReady === 'true') {
        setTimeout(() => {
          const success = onImportFromTangent(false);
          if (success) {
            Message.success("已自动导入相切计算的点位数据");
          }
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // persist on change
  useEffect(() => {
    saveAll({ oNum, phiMax, phiMin, phiCut, mx, rows });
  }, [oNum, phiMax, phiMin, phiCut, mx, rows]);

  // 监听 Tab 激活状态，检测待导入数据
  useEffect(() => {
    if (isActive) {
      const importReady = localStorage.getItem(IMPORT_READY_KEY);
      if (importReady === 'true') {
        // 延迟导入，确保组件已完全渲染
        setTimeout(() => {
          const success = onImportFromTangent(false);
          if (success) {
            Message.success("已自动导入相切计算的点位数据");
          }
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const onCopyDownPhi = (idx) => {
    if (idx >= rows.length - 1) return;
    setRows((prev) => {
      const next = prev.slice();
      next[idx + 1] = { ...next[idx + 1], phi: next[idx].phi };
      return next;
    });
  };
  const onCopyDownLen = (idx) => {
    if (idx >= rows.length - 1) return;
    setRows((prev) => {
      const next = prev.slice();
      next[idx + 1] = { ...next[idx + 1], len: next[idx].len };
      return next;
    });
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  // 从相切计算导入点位数据
  // showMessage: 是否显示提示消息
  // useLocalStorageStartRow: 是否使用localStorage中保存的起始行号（自动导入时为true，手动导入时为false）
  const onImportFromTangent = (showMessage = true, useLocalStorageStartRow = true) => {
    try {
      const raw = localStorage.getItem(IMPORT_KEY);
      if (!raw) {
        if (showMessage) {
          Message.warning("没有可导入的数据，请先在相切计算页面计算并点击导入按钮");
        }
        return false;
      }
      const importData = JSON.parse(raw);
      if (!Array.isArray(importData) || importData.length === 0) {
        if (showMessage) {
          Message.warning("导入数据格式不正确");
        }
        return false;
      }

      // 读取起始行号
      let startRow = importStartRow; // 默认使用当前state的值
      if (useLocalStorageStartRow) {
        // 自动导入时，优先使用localStorage中保存的值
        const savedStartRow = localStorage.getItem(IMPORT_START_ROW_KEY);
        if (savedStartRow) {
          startRow = parseInt(savedStartRow, 10);
        }
      }

      // 确保起始行号在有效范围内
      const validStartRow = Math.max(1, Math.min(20, startRow));

      // 从指定行开始导入（startRow对应索引startRow-1）
      setRows((prev) => {
        const next = prev.slice();
        for (let i = 0; i < importData.length && (validStartRow - 1 + i) < 20; i++) {
          const d = importData[i];
          next[validStartRow - 1 + i] = {
            func: d.func || "G01",
            phi: Number.isFinite(Number(d.phi)) ? Number(d.phi) : 0,
            len: Number.isFinite(Number(d.len)) ? Number(d.len) : 0,
            r: Number.isFinite(Number(d.r)) ? Number(d.r) : 0,
          };
        }
        return next;
      });

      if (showMessage) {
        const endRow = Math.min(20, validStartRow + importData.length - 1);
        Message.success(`成功导入 ${importData.length} 个点位数据到 A${validStartRow}-A${endRow}`);
      }

      // 清除导入就绪标记
      localStorage.removeItem(IMPORT_READY_KEY);
      return true;
    } catch (e) {
      if (showMessage) {
        Message.error("导入失败：" + e.message);
      }
      return false;
    }
  };

  const validate = () => {
    const o = Number(oNum);
    const pMax = Number(phiMax);
    const pMin = Number(phiMin);
    const pCut = Number(phiCut);
    const mxx = Number(mx);
    if (!Number.isFinite(o) || o < 7001 || o > 7999) return false;
    if (!Number.isFinite(pMax) || pMax <= 0) return false;
    if (!Number.isFinite(pMin) || pMin <= 0) return false;
    if (!Number.isFinite(pCut) || pCut <= 0) return false;
    if (!Number.isFinite(mxx) || mxx < 1) return false;
    return true;
  };

  const validateWithDetails = () => {
    const errors = [];
    const o = Number(oNum);
    const pMax = Number(phiMax);
    const pMin = Number(phiMin);
    const pCut = Number(phiCut);
    const mxx = Number(mx);

    if (!Number.isFinite(o) || o < 7001 || o > 7999) {
      if (!Number.isFinite(o) || isNaN(o)) {
        errors.push("修整形状程序号 O 未填写或格式不正确");
      } else {
        errors.push("修整形状程序号 O 超出范围，应为 7001~7999");
      }
    }

    if (!Number.isFinite(pMax) || pMax <= 0) {
      if (!Number.isFinite(pMax) || isNaN(pMax)) {
        errors.push("最大直径 φmax 未填写或格式不正确");
      } else {
        errors.push("最大直径 φmax 应大于 0");
      }
    }

    if (!Number.isFinite(pMin) || pMin <= 0) {
      if (!Number.isFinite(pMin) || isNaN(pMin)) {
        errors.push("最小直径 φmin 未填写或格式不正确");
      } else {
        errors.push("最小直径 φmin 应大于 0");
      }
    }

    if (!Number.isFinite(pCut) || pCut <= 0) {
      if (!Number.isFinite(pCut) || isNaN(pCut)) {
        errors.push("对刀点直径 φ对刀点 未填写或格式不正确");
      } else {
        errors.push("对刀点直径 φ对刀点 应大于 0");
      }
    }

    if (!Number.isFinite(mxx) || mxx < 1) {
      if (!Number.isFinite(mxx) || isNaN(mxx)) {
        errors.push("安全退刀富裕量 mx 未填写或格式不正确");
      } else {
        errors.push("安全退刀富裕量 mx 应≥1");
      }
    }

    return errors;
  };

  const genNumber = (n) => (Number.isFinite(n) ? n : 0);

  const generate = () => {
    checkPasswordBeforeAction(() => {
      const validationErrors = validateWithDetails();
      if (validationErrors.length > 0) {
        const errorMessage = "请检查以下参数：\n" + validationErrors.map(err => "• " + err).join("\n");
        setProgram(errorMessage);
        return;
      }
    const o = Number(oNum);
    const pMax = Number(phiMax);
    const pMin = Number(phiMin);
    const pCut = Number(phiCut);
    const mxx = Number(mx);

    const delta = Math.round(pMax - pMin + mxx); // ¢max - ¢min + mx，取整避免数控系统报警

    // Normalize row data
    const rs = rows.map((r) => ({
      func: r.func || "04",
      phi: genNumber(r.phi),
      len: genNumber(r.len),
      r: genNumber(r.r),
    }));

    // Header and setup
    const lines = [];
    lines.push("%");
    lines.push(`O${o}(Xiu Zheng Xing Zhuang ${o - 7000})`);
    lines.push("M98P9090");
    lines.push(`#970=#590+${delta}`);
    lines.push(`#965=#590+${pCut}`);

    // 直径宏：
    // A1..A10 -> #551..#560，A11..A20 -> #161..#170
    // 规则：
    // - A1(#551) 必出；
    // - 其余仅当该行功能不为“无”且直径与上一行不同时输出。
    const diaMacroOf = (idx) => {
      // idx: 0-based (A1 为 0)
      if (idx <= 9) return 551 + idx;
      return 161 + (idx - 10);
    };
    for (let i = 0; i < 20; i++) {
      const needPhiMacro =
        i === 0 || (rs[i].func !== "04" && Number(rs[i].phi) !== Number(rs[i - 1].phi));
      if (needPhiMacro) {
        lines.push(`#${diaMacroOf(i)}=#965-${rs[i].phi}`);
      }
    }

    // 长度宏：
    // - #561 固定为 A1 的基准（用于初始 Z#561）
    // - A2..A9 -> #562..#569；A10 -> #969；A11..A20 -> #171..#180
    // 规则：仅当该行功能不为"无"且长度与上一行不同，才输出对应宏。
    lines.push(`#561=#591-#523-2`);
    const lenMacroOf = (idx) => {
      // idx: 0-based
      if (idx === 0) return 561; // not used in per-line, but defined above
      if (idx >= 1 && idx <= 8) return 561 + idx; // 562..569 for A2..A9
      if (idx === 9) return 969; // A10
      // A11..A20
      return 171 + (idx - 10); // 171..180
    };
    for (let i = 1; i < 20; i++) {
      const needLenMacro = rs[i].func !== "04" && Number(rs[i].len) !== Number(rs[i - 1].len);
      if (needLenMacro) {
        // 如果长度为0，使用#591-#589；否则使用#591-长度值
        const lenFormula = rs[i].len === 0 ? "#591-#589" : `#591-${rs[i].len}`;
        lines.push(`#${lenMacroOf(i)}=${lenFormula}`);
      }
    }

    // Motion block
    lines.push("G04");
    lines.push("M40");
    lines.push("G01G90G94X#970F5000");
    lines.push("M41");
    lines.push("Z#561F4000");
    lines.push("X#551F3000");

    const lineFor = (i) => {
      // i = 1..19 corresponds to A2..A20
      const row = rs[i];
      const prev = rs[i - 1];
      if (!row || row.func === "04") return null;
      const g = row.func; // 'G01' | 'G02' | 'G03'
      const parts = [`${g}G90G94`];
      // X（当直径变化时输出）
      if (row.phi !== prev.phi) {
        const xm = (i <= 9) ? (551 + i) : (161 + (i - 10));
        parts.push(`X#${xm}`);
      }
      // Z（当长度变化时输出）
      if (row.len !== prev.len) {
        const zm = (() => {
          if (i >= 1 && i <= 8) return 561 + i; // 562..569
          if (i === 9) return 969; // A10
          return 171 + (i - 10); // 171..180 for A11..A20
        })();
        parts.push(`Z#${zm}`);
      }
      // R（当 R>0 时输出）
      if (Number(row.r) > 0) parts.push(`R${row.r}`);
      parts.push("F#509");
      return parts.join("");
    };

    for (let i = 1; i < 20; i++) {
      const l = lineFor(i);
      if (l) lines.push(l);
    }

    // Closing block
    lines.push("G02G91G94X-[#589*2]Z#589R#589F#509");
    lines.push("G01G91G94X-#580F#508");
    lines.push("G01G91G94Z2F300");
    lines.push("M40");
    lines.push("G01G90G94X#970F5000");
    lines.push("G04");
    lines.push("#590=#590-#592");
    lines.push("#591=#591-#593");
    lines.push("#518=#518-#592");
    lines.push("#510=#510-#592");
    lines.push("#193=#593-[#592/2*TAN[#966]]");
    lines.push("G04");
    lines.push("#511=#511-#193");
    lines.push("#599=#599-#592");
    lines.push("G04");
    lines.push("M99");
    lines.push("%");

      setProgram(lines.join("\n"));
    });
  };

  const onDownload = (withTxtExt = true) => {
    if (!program) return;
    const o = Number(oNum) || 0;
    const filename = withTxtExt ? `O${o}.txt` : `O${o}`;
    const mime = withTxtExt ? "text/plain;charset=utf-8" : "application/octet-stream";
    // Use File to carry filename metadata for better cross-browser behavior
    const file = new File([program], filename, { type: mime });
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", filename);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const canGenerate = useMemo(() => validate(), [oNum, phiMax, phiMin, phiCut, mx, rows]);

  return (
    <div className="cpn-wrapper-vertical" style={{ paddingLeft: 20, paddingRight: 20 }}>
      <Typography.Title heading={5}>修砂轮程序生成</Typography.Title>
      <Form layout="vertical">
        <Row gutter={12}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="修整形状程序号 O (7001~7999)">
              <InputNumber
                placeholder=""
                min={7001}
                max={7999}
                size="large"
                value={oNum}
                onChange={(v) => {
                  setONum(v);
                  const n = Number(v);
                  if (Number.isFinite(n) && (n < 7001 || n > 7999)) {
                    Message.warning("程序号超出范围：应为 7001~7999");
                  }
                }}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="最大直径 φmax (>0)">
              <div style={{ display: "flex", alignItems: "center" }}>
                <InputNumber
                  placeholder=""
                  min={0}
                  size="large"
                  value={phiMax}
                  onChange={(v) => {
                    setPhiMax(v);
                    const n = Number(v);
                    if (Number.isFinite(n) && n <= 0) {
                      Message.warning("φmax 超出范围：应大于 0");
                    }
                  }}
                  style={{ width: "100%" }}
                />
                <Button
                  size="mini"
                  style={{ marginLeft: 6 }}
                  onClick={() => setPhiCut(phiMax)}
                  title="同步到 φ对刀点"
                >
                  <IconCopy />
                </Button>
              </div>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="最小直径 φmin (>0)">
              <InputNumber
                placeholder=""
                min={0}
                size="large"
                value={phiMin}
                onChange={(v) => {
                  setPhiMin(v);
                  const n = Number(v);
                  if (Number.isFinite(n) && n <= 0) {
                    Message.warning("φmin 超出范围：应大于 0");
                  }
                }}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="对刀点直径 φ对刀点 (>0)">
              <InputNumber
                placeholder=""
                min={0}
                size="large"
                value={phiCut}
                onChange={(v) => {
                  setPhiCut(v);
                  const n = Number(v);
                  if (Number.isFinite(n) && n <= 0) {
                    Message.warning("φ对刀点 超出范围：应大于 0");
                  }
                }}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={12} md={6}>
            <Form.Item label="安全退刀富裕量 mx (≥1)">
              <InputNumber
                placeholder=""
                min={1}
                size="large"
                value={mx}
                onChange={(v) => {
                  setMx(v);
                  const n = Number(v);
                  if (Number.isFinite(n) && n < 1) {
                    Message.warning("mx 超出范围：应≥1");
                  }
                }}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider />

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
        <Typography.Text strong>位置点参数表格</Typography.Text>
        <Button
          type="primary"
          size="small"
          icon={<IconImport />}
          onClick={() => onImportFromTangent(true, false)}
        >
          从相切计算导入
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 14 }}>从 A</span>
          <InputNumber
            size="small"
            min={1}
            max={20}
            value={importStartRow}
            onChange={setImportStartRow}
            style={{ width: 60 }}
          />
          <span style={{ fontSize: 14 }}>开始</span>
        </div>
      </div>
      <div style={{ overflowX: "auto", width: "90vw", maxWidth: 720 }}>
        <div style={{ minWidth: 720 }}>
          <Row gutter={8} style={{ fontWeight: 600, marginBottom: 8 }}>
            <Col span={2}>位置点/项目</Col>
            <Col span={3}>功能</Col>
            <Col span={6}>直径 φ</Col>
            <Col span={6}>长度 L</Col>
            <Col span={4}>圆弧 R</Col>
          </Row>
          {rows.map((r, i) => (
            <Row key={i} gutter={8} align="center" style={{ marginBottom: 6 }}>
              <Col span={2}>A{i + 1}</Col>
              <Col span={3}>
                <Select
                  size="large"
                  value={r.func}
                  onChange={(v) => updateRow(i, { func: v })}
                  style={{ width: "100%" }}
                >
                  <Option value="04">无</Option>
                  <Option value="G01">G01</Option>
                  <Option value="G02">G02</Option>
                  <Option value="G03">G03</Option>
                </Select>
              </Col>
              <Col span={6}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <InputNumber
                    size="large"
                    placeholder="默认 0"
                    value={r.phi}
                    onChange={(v) => updateRow(i, { phi: v })}
                    style={{ width: "100%" }}
                  />
                  <Button
                    size="mini"
                    style={{ marginLeft: 6 }}
                    onClick={() => onCopyDownPhi(i)}
                    disabled={i >= rows.length - 1}
                    title="将当前直径同步到下一行"
                  >
                    <IconCaretDown />
                  </Button>
                </div>
              </Col>
              <Col span={6}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <InputNumber
                    size="large"
                    placeholder="默认 0"
                    value={r.len}
                    onChange={(v) => updateRow(i, { len: v })}
                    style={{ width: "100%" }}
                  />
                  <Button
                    size="mini"
                    style={{ marginLeft: 6 }}
                    onClick={() => onCopyDownLen(i)}
                    disabled={i >= rows.length - 1}
                    title="将当前长度同步到下一行"
                  >
                    <IconCaretDown />
                  </Button>
                </div>
              </Col>
              <Col span={4}>
                <InputNumber
                  size="large"
                  placeholder="默认 0"
                  value={r.r}
                  onChange={(v) => updateRow(i, { r: v })}
                  style={{ width: "100%" }}
                />
              </Col>
              {/* <Col span={7}>
                <Button onClick={() => onCopyPrev(i)} disabled={i === 0}>
                  同步上一行
                </Button>
              </Col> */}
            </Row>
          ))}
        </div>
      </div>

      <Space style={{ marginTop: 12 }}>
        <Tooltip
          content={canGenerate ? "" : "当前没有输全参数或输错参数，请检查"}
          disabled={canGenerate}
        >
          <Button type="primary" onClick={generate}>
            程序生成
          </Button>
        </Tooltip>
      </Space>

      <TextArea
        id="dp_program"
        style={{ minHeight: "16em", width: "95%", marginTop: 12 }}
        value={program}
        onChange={setProgram}
        placeholder={"%\nO7001(...)\nM98P9090\n...\nM99\n%"}
      />

      <Space style={{ marginTop: 10, justifyContent: "center", marginBottom: 20 }} wrap>
        <Button onClick={() => onDownload(true)} disabled={!program}>
          另存为 txt 文件
        </Button>
        <Button onClick={() => onDownload(false)} disabled={!program}>
          另存为存储卡文件
        </Button>
        <Button icon={<IconCopy />} onClick={() => copy(null, "#dp_program")} disabled={!program}>
          复制
        </Button>
      </Space>
    </div>
  );
};

export default DresserProgramGenerator;
